/**
 * Central place for Vite env (`VITE_*`). Used by wallet, gallery, and future stake/unstake calls.
 * @see staking/.env.example
 */

function trim(v) {
  return String(v ?? "").trim();
}

/** @param {string} s */
export function isHexAddress(s) {
  return /^0x[a-fA-F0-9]{40}$/.test(s);
}

/**
 * @typedef {Object} StakingEnv
 * @property {string} projectId          Reown Cloud project id
 * @property {number} chainId            EVM chain id (default 1 mainnet)
 * @property {string} owlexNft            Owlex ERC-721
 * @property {string} stakingContract    OwlexStaking
 * @property {string} owlexXp            Owlex XP ERC-20
 * @property {string} alchemyApiKey       Alchemy key (NFT API — still used client-side for NFT REST)
 * @property {string} ethereumRpcUrl     Optional RPC for wallet (Reown / wagmi)
 * @property {string} mainnetRpcProxy    Same-origin path for JSON-RPC proxy (no secret in bundle)
 * @property {boolean} roostAlchemyTransfers If true, call `alchemy_getAssetTransfers` for Roost (extra Alchemy CU). Default false — eth_getLogs + stakeInfo only.
 * @property {string} roostAlchemySupplement Roost reconstruction: `fallback` (default) and `always` union Alchemy + eth_getLogs; `alchemy_only` skips log scans when Alchemy returned any IDs (can under-count).
 * @property {string} traitSpinContract  Phase 2 — optional
 * @property {string} traitMarketplaceContract Phase 2 — optional
 * @property {string} compositorUrl Same-origin proxy path `/api/compositor` (default) or full Worker URL if CORS allows
 * @property {string} metadataOrigin Public base for token images + JSON (default https://metadata.owlex.art)
 * @property {string} cfImagesBaseUrl Cloudflare Images delivery base (`https://imagedelivery.net/{account_hash}`) for trait layer previews
 * @property {string} cfImagesVariant CF Images variant name (default public)
 */

/**
 * XP leaderboard (modal): off on Cloudflare preview URLs by default; on for production host + local dev.
 *
 * - Set `VITE_XP_LEADERBOARD_DISABLED=1` to hide everywhere (kill switch).
 * - Set `VITE_XP_LEADERBOARD_ALLOWED_HOSTS` to a comma list to override (e.g. add a preview hostname for QA).
 * - If that variable is **unset**, only `staking.owlex.art`, `localhost`, and `127.0.0.1` are allowed.
 *
 * @returns {boolean}
 */
export function isXpLeaderboardEnabledForHost() {
  if (typeof window === "undefined") return false;
  const off = trim(import.meta.env.VITE_XP_LEADERBOARD_DISABLED ?? "");
  if (off === "1" || /^true|yes$/i.test(off)) return false;

  const h = window.location.hostname.toLowerCase();
  if (h === "localhost" || h === "127.0.0.1") return true;

  const rawHosts = trim(import.meta.env.VITE_XP_LEADERBOARD_ALLOWED_HOSTS ?? "");
  if (rawHosts) {
    const set = rawHosts
      .split(",")
      .map((s) => trim(s).toLowerCase())
      .filter(Boolean);
    return set.includes(h);
  }

  return h === "staking.owlex.art";
}

/** @returns {StakingEnv} */
export function getStakingEnv() {
  const chainRaw = trim(import.meta.env.VITE_CHAIN_ID);
  const chainId = chainRaw ? Number.parseInt(chainRaw, 10) : 1;
  return {
    projectId: trim(import.meta.env.VITE_PROJECT_ID),
    chainId: Number.isFinite(chainId) && chainId > 0 ? chainId : 1,
    owlexNft: trim(import.meta.env.VITE_OWLEX_NFT),
    stakingContract: trim(import.meta.env.VITE_STAKING_CONTRACT),
    owlexXp: trim(import.meta.env.VITE_OWLEX_XP),
    alchemyApiKey: trim(import.meta.env.VITE_ALCHEMY_API_KEY),
    ethereumRpcUrl: trim(import.meta.env.VITE_ETHEREUM_RPC_URL),
    mainnetRpcProxy: trim(import.meta.env.VITE_MAINNET_RPC_PROXY),
    roostAlchemySupplement: trim(
      import.meta.env.VITE_ROOST_ALCHEMY_SUPPLEMENT || "fallback"
    ).toLowerCase(),
    roostAlchemyTransfers: (() => {
      const v = trim(import.meta.env.VITE_ROOST_ALCHEMY_TRANSFERS).toLowerCase();
      return v === "1" || v === "true" || v === "yes";
    })(),
    traitSpinContract: trim(import.meta.env.VITE_TRAIT_SPIN_CONTRACT),
    traitMarketplaceContract: trim(import.meta.env.VITE_TRAIT_MARKETPLACE_CONTRACT),
    compositorUrl: trim(import.meta.env.VITE_COMPOSITOR_URL) || "/api/compositor",
    metadataOrigin: trim(import.meta.env.VITE_METADATA_ORIGIN) || "https://metadata.owlex.art",
    cfImagesBaseUrl: trim(import.meta.env.VITE_CF_IMAGES_BASE_URL ?? ""),
    cfImagesVariant: trim(import.meta.env.VITE_CF_IMAGES_VARIANT ?? "") || "public",
  };
}

/** NFT gallery (Alchemy + contract) */
export function envHasNftGallery(env) {
  return isHexAddress(env.owlexNft) && env.alchemyApiKey.length > 0;
}

/** On-chain staking + XP (for stake / unstake / balances) */
export function envHasStakingContracts(env) {
  return isHexAddress(env.stakingContract) && isHexAddress(env.owlexXp);
}

/**
 * Absolute URL for same-origin JSON-RPC proxy (`eth_getLogs`, etc.). Never embeds API keys — use
 * `VITE_MAINNET_RPC_PROXY` (e.g. `/api/eth`) plus server `ETH_RPC_URL` (Vite dev / Cloudflare Pages).
 * @returns {string} empty if unset (caller uses wagmi public RPC for log scans)
 */
export function getMainnetRpcProxyAbsoluteUrl() {
  const raw = trim(import.meta.env.VITE_MAINNET_RPC_PROXY ?? "");
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (typeof window === "undefined") return "";
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return `${window.location.origin}${path}`;
}

/**
 * If production build has no `VITE_PROJECT_ID`, show a visible banner (Vite inlines env at build time).
 * Call from browser entrypoints after `document.body` exists.
 */
export function mountProdEnvBannerIfNeeded() {
  if (typeof document === "undefined") return;
  if (!import.meta.env.PROD) return;
  const e = getStakingEnv();
  if (e.projectId) return;
  if (document.getElementById("owlex-deploy-env-banner")) return;
  const banner = document.createElement("div");
  banner.id = "owlex-deploy-env-banner";
  banner.className = "deploy-env-banner";
  banner.setAttribute("role", "alert");
  banner.textContent =
    "Wallet connect is not configured: VITE_PROJECT_ID was missing when this site was built. Rebuild with staking/.env (or set env vars in Cloudflare Pages), redeploy, and add this exact URL under Reown Cloud → your project → Domains.";
  document.body.insertBefore(banner, document.body.firstChild);
}

/** Log once per load if something is missing (dev-friendly). */
export function warnStakingEnvIfIncomplete() {
  const e = getStakingEnv();
  if (!e.projectId) {
    console.warn("[owlex-staking] Missing VITE_PROJECT_ID — wallet connect will not work.");
  }
  if (!envHasNftGallery(e)) {
    console.warn(
      "[owlex-staking] Missing VITE_OWLEX_NFT and/or VITE_ALCHEMY_API_KEY — gallery won’t load."
    );
  }
  if (!envHasStakingContracts(e)) {
    console.warn(
      "[owlex-staking] Missing VITE_STAKING_CONTRACT and/or VITE_OWLEX_XP — set these after deploy for stake/unstake & XP UI."
    );
  }
  if (envHasStakingContracts(e) && !e.mainnetRpcProxy) {
    console.warn(
      "[owlex-staking] Roost uses eth_getLogs; set VITE_MAINNET_RPC_PROXY=/api/eth and ETH_RPC_URL (server) for reliable scans without exposing keys in the bundle."
    );
  }
}
